import React, { useState, useRef } from "react";
import { uploadImage, errMsg } from "../api";

/** Tiny reusable building blocks for admin forms. */

export const Input = ({ label, value, onChange, type = "text", placeholder, testid }) => (
    <label className="block">
        {label && <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/90 font-semibold">{label}</span>}
        <input
            data-testid={testid}
            type={type}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 w-full bg-[#1A1410] text-[#F5F2EB] border border-[#F5F2EB]/15 focus:border-[#D4A437] outline-none px-3 py-2 font-body text-sm"
        />
    </label>
);

export const Textarea = ({ label, value, onChange, rows = 3, testid }) => (
    <label className="block">
        {label && <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/90 font-semibold">{label}</span>}
        <textarea
            data-testid={testid}
            value={value ?? ""}
            rows={rows}
            onChange={(e) => onChange(e.target.value)}
            className="mt-1 w-full bg-[#1A1410] text-[#F5F2EB] border border-[#F5F2EB]/15 focus:border-[#D4A437] outline-none px-3 py-2 font-body text-sm leading-relaxed resize-y"
        />
    </label>
);

export const Button = ({ children, onClick, type = "button", variant = "primary", testid, disabled }) => {
    const cls =
        variant === "primary"
            ? "bg-[#D4A437] text-[#1A1410]"
            : variant === "danger"
            ? "bg-[#C65A2C] text-[#F5F2EB]"
            : "bg-transparent border border-[#F5F2EB]/20 text-[#F5F2EB]";
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`${cls} px-4 py-2 font-display uppercase tracking-[0.2em] text-xs font-bold active:opacity-80 disabled:opacity-40`}
        >
            {children}
        </button>
    );
};

export const Card = ({ children, title, testid }) => (
    <div data-testid={testid} className="bg-[#241B16] border border-[#F5F2EB]/10 p-4 sm:p-5">
        {title && (
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4A437] font-semibold mb-3">
                {title}
            </div>
        )}
        {children}
    </div>
);

/** Image picker: file upload OR paste a URL. */
export const ImagePicker = ({ value, onChange, label = "Photo", testid }) => {
    const inputRef = useRef(null);
    const fileId = React.useId();
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr("");
        setUploading(true);
        try {
            const { url } = await uploadImage(file);
            onChange(url);
        } catch (ex) {
            setErr(errMsg(ex));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437]/90 font-semibold mb-1">{label}</div>

            {value && (
                <div className="mb-2 relative w-full aspect-video bg-[#1A1410] overflow-hidden border border-[#F5F2EB]/10">
                    <img src={value} alt="preview" className="w-full h-full object-cover" />
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="absolute top-2 right-2 bg-black/70 text-white text-[10px] uppercase tracking-widest px-2 py-1"
                    >
                        Remove
                    </button>
                </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="url"
                    placeholder="Paste image URL"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="flex-1 bg-[#1A1410] text-[#F5F2EB] border border-[#F5F2EB]/15 px-3 py-2 text-sm"
                />
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    className="hidden"
                    id={fileId}
                />
                <label
                    htmlFor={fileId}
                    className="bg-transparent border border-[#D4A437]/70 text-[#D4A437] px-4 py-2 font-display uppercase tracking-[0.2em] text-xs font-bold cursor-pointer text-center active:opacity-80"
                >
                    {uploading ? "Uploading…" : "Upload File"}
                </label>
            </div>
            {err && <div className="mt-1 text-[11px] text-[#C65A2C]">{err}</div>}
        </div>
    );
};

export const Toast = ({ message, kind = "success" }) => {
    if (!message) return null;
    const cls = kind === "error" ? "bg-[#C65A2C]" : "bg-[#3a6b3f]";
    return (
        <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 ${cls} text-[#F5F2EB] text-xs uppercase tracking-[0.2em] font-display font-bold px-4 py-2 shadow-lg`}>
            {message}
        </div>
    );
};
